/* eslint-disable react/prop-types */
import React, {
  useEffect,
  useState,
  useCallback,
} from 'react';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Fab, { FabProps } from '@material-ui/core/Fab';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import throttle from 'lodash/throttle';

import useMobileButton from 'components/chat/utils/useMobileButton';
import useEnableExhibitorPanelTreatment from 'hooks/useEnableExhibitorPanelTreatment';
import useChatVisibility from './twilio/hooks/useChatVisibility';

const StyledFab = withStyles(() => ({
  root: {
    position: 'fixed',
    right: '24px',
    bottom: ({ bottom }: { bottom: number }) => `${bottom}px`,
    transform: ({ bottom }: { bottom: number }) => `translateY(${60 + bottom}px)`,
    transition: 'transform 400ms',
    zIndex: 2,
  },
}))(Fab);

const useActiveStyles = makeStyles(() => ({
  active: {
    transform: 'translateY(0)',
  },
}));

export const useFabBottomValue = () => {
  const twilioVisible = useChatVisibility();
  const chatroomAvailable = twilioVisible;
  const isDesktop = useMediaQuery('(min-width: 960px)');
  const mobileChatButtonRendered = useMobileButton();
  const showExhibitorPanel = useEnableExhibitorPanelTreatment();

  if (!chatroomAvailable) return 24;
  if (isDesktop) {
    if (showExhibitorPanel) {
      return 104;
    }
    return 72;
  }
  if (!mobileChatButtonRendered) return 24;
  return 96;
};

type Props = {
  scrollableTarget?: HTMLElement;
  className?: string;
  size?: 'large' | 'medium' | 'small';
} | FabProps;

const BackToTopButton = React.forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    scrollableTarget = document.body,
    className = '',
    size = 'medium',
    ...other
  } = props as any;
  const [showBtn, setShowBtn] = useState(false);
  const activeStyleClasses = useActiveStyles();

  const onClick = () => {
    scrollableTarget.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onScrollHandler = useCallback(
    throttle(() => {
      setShowBtn(scrollableTarget.scrollTop !== 0);
    }, 200),
    [scrollableTarget],
  );

  useEffect(() => {
    scrollableTarget.addEventListener('scroll', onScrollHandler);

    return () => {
      scrollableTarget.removeEventListener('scroll', onScrollHandler);
    };
  }, [onScrollHandler, scrollableTarget]);

  const fabBottomValue = useFabBottomValue();

  return (
    <StyledFab
      {...other}
      className={`${showBtn ? activeStyleClasses.active : ''} ${className}`}
      size={size}
      bottom={fabBottomValue}
      onClick={onClick}
      ref={ref}
    >
      <ArrowUpwardIcon />
    </StyledFab>
  );
});

export default BackToTopButton;
